import { useEffect } from "react";
import { TextInput, Select, Stack, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  required,
  maxLength,
  compose,
  scrollToFirstError,
} from "../../../utils/formValidation";
import { useSourcesAdmin } from "../../../hooks/admin/useSources";
import { useSourceSubsites } from "../../../hooks/admin/useSourceSubsites";
import { SpecimenMacro } from "../../../services/admin/specimenMacro.service";

const MATERIAL_OPTIONS = [
  { value: "formalin_jar", label: "Formalin jar" },
  { value: "block", label: "Block" },
  { value: "slides_only", label: "Slides only" },
  { value: "blood_tube", label: "Blood tube" },
  { value: "urine_sample", label: "Urine sample" },
  { value: "stool_collection", label: "Stool collection" },
].sort((a, b) => a.label.localeCompare(b.label));

export interface SpecimenMacroFormValues {
  code: string;
  description: string;
  sourceId: string | null;
  subsiteId: string | null;
  findings: string;
  collectionMethod: string;
  material: string | null;
  isActive: boolean;
}

interface SpecimenMacroFormProps {
  formId: string;
  initialValues?: SpecimenMacro;
  onSubmit: (values: SpecimenMacroFormValues) => void;
}

export default function SpecimenMacroForm({
  formId,
  initialValues,
  onSubmit,
}: SpecimenMacroFormProps) {
  const { data: sources = [] } = useSourcesAdmin();
  const { data: allSubsites = [] } = useSourceSubsites();

  const form = useForm<SpecimenMacroFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      sourceId: initialValues?.sourceId || null,
      subsiteId: initialValues?.subsiteId || null,
      findings: initialValues?.findings || "",
      collectionMethod: initialValues?.collectionMethod || "",
      material: initialValues?.material || null,
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      code: compose(required("Code is required"), maxLength(20)),
    },
  });

  const selectedSourceId = form.values.sourceId;

  useEffect(() => {
    if (selectedSourceId && initialValues?.sourceId !== selectedSourceId) {
      form.setFieldValue("subsiteId", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceId]);

  const sourceOptions = sources
    .filter((s: any) => s.isActive !== false)
    .map((s: any) => ({ value: s.id, label: s.name }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  const subsiteOptions = allSubsites
    .filter(
      (ss: any) => ss.sourceId === selectedSourceId && ss.isActive !== false
    )
    .map((ss: any) => ({ value: ss.id, label: ss.name }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));

  const handleSubmit = form.onSubmit(
    (values) => {
      onSubmit({
        ...values,
        code: values.code.toUpperCase().trim(),
      });
    },
    () => scrollToFirstError(form)
  );

  return (
    <form id={formId} onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Code"
          placeholder="e.g., D2, DBP, EGJU"
          withAsterisk
          {...form.getInputProps("code")}
          onChange={(e) =>
            form.setFieldValue("code", e.target.value.toUpperCase())
          }
        />

        <TextInput
          label="Description"
          placeholder="e.g., Duodenum 2nd Part - Biopsy"
          {...form.getInputProps("description")}
        />

        <Select
          label="Source (Site)"
          placeholder="Select source..."
          data={sourceOptions}
          searchable
          clearable
          {...form.getInputProps("sourceId")}
        />

        <Select
          label="Subsite"
          placeholder={
            selectedSourceId ? "Select subsite..." : "Select a source first"
          }
          data={subsiteOptions}
          searchable
          clearable
          disabled={!selectedSourceId}
          {...form.getInputProps("subsiteId")}
        />

        <TextInput
          label="Findings"
          placeholder="e.g., Polyp, Ulcer, MASS"
          {...form.getInputProps("findings")}
        />

        <TextInput
          label="Collection Method / Procedure"
          placeholder="e.g., Biopsy, Polypectomy"
          {...form.getInputProps("collectionMethod")}
        />

        <Select
          label="Material"
          placeholder="Select material..."
          data={MATERIAL_OPTIONS}
          clearable
          {...form.getInputProps("material")}
        />

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
